import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Divider, Input, Spin, Tabs, Upload, UploadProps } from 'antd';
import XMLViewer from 'react-xml-viewer';
import { tablesSlice } from '../../../../TablesSlice';
import { useAppDispatch, useAppSelector } from '../../../../../../core/redux';
import { projectsApiService } from '../../../../../../api/projects/projectsApiService';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import { notificationMessages, showNotification } from '../../../../../usersPage/helpers/helpers';
import { getErrors } from '../../../../../../helpers/helpers';
import s from './CDARedactor.module.scss';

type TargetKey = React.MouseEvent | React.KeyboardEvent | string;

const { setCdaRedactorTabs, setActiveXmlTab } = tablesSlice.actions;

export const RenderTabCDARedactor = ({
  id,
  code,
  initialHeaderTitle,
}: {
  id: string;
  code: string;
  initialHeaderTitle: string;
}) => {
  const { notification } = App.useApp();
  const dispatch = useAppDispatch();

  const [isEditTitle, setIsEditTitle] = useState(false);
  const [headerTitle, setHeaderTitle] = useState(initialHeaderTitle);

  const { cdaRedactorTabs } = useAppSelector((state) => state.tablesSlice);
  const [uploadXml, { data: parseXmlData, error: parseXmlError, isLoading: parseXmlLoading }] =
    projectsApiService.useParseXmlDocMutation();

  const props: UploadProps = {
    name: 'file',
    showUploadList: false,
    customRequest: (e) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (!e.file.type.match('text/xml')) {
        showNotification(notification, 'Неверный формат файла', '', 'error');
        return;
      }
      const data = new FormData();
      data.append('file', e.file);
      uploadXml(data);
    },
  };

  const handleDblClickHeaderTab = (e) => {
    if (e.detail === 2) {
      setIsEditTitle(true);
    }
  };

  const handleChangeHeaderTab = (e) => {
    setHeaderTitle(e.target.value);
  };

  const handleChangeTabTitlePressInter = () => {
    let newTabs = [...cdaRedactorTabs];
    const currentTab = cdaRedactorTabs.find(({ key }) => key === id);
    if (currentTab) {
      newTabs = cdaRedactorTabs.filter(({ key }) => key !== id);
      newTabs.push({ ...currentTab, label: headerTitle });
    }
    dispatch(setCdaRedactorTabs(newTabs));
    setIsEditTitle(false);
  };
  const handleChangeTab = (code, title) => {
    let newTabs = [...cdaRedactorTabs];
    const currentTab = cdaRedactorTabs.find(({ key }) => key === id);
    if (currentTab) {
      newTabs = cdaRedactorTabs.filter(({ key }) => key !== id);
      newTabs.push({ ...currentTab, label: title, fileName: title, code: code });
    }
    dispatch(setCdaRedactorTabs(newTabs));
    setIsEditTitle(false);
  };
  const { parseXmlError: parseXmlErrorText } = getErrors({ parseXmlError });

  useEffect(() => {
    if (parseXmlErrorText) {
      showNotification(notification, notificationMessages.errUploadXml, parseXmlErrorText, 'error');
    }
  }, [parseXmlError]);
  useEffect(() => {
    if (initialHeaderTitle) {
      setHeaderTitle(initialHeaderTitle);
    }
  }, [initialHeaderTitle]);
  useEffect(() => {
    if (parseXmlData && !parseXmlError && !parseXmlLoading) {
      handleChangeTab(parseXmlData.data, parseXmlData.filename);
    }
  }, [parseXmlData, parseXmlError, parseXmlLoading]);
  return (
    <div className={s.TabCDARedactor}>
      <Spin tip='Loading...' spinning={parseXmlLoading} size='large'>
        <div className={s.tabHeader}>
          <div className={s.tabHeaderContent}>
            {isEditTitle ? (
              <Input
                className={s.label}
                onChange={handleChangeHeaderTab}
                onBlur={handleChangeTabTitlePressInter}
                value={headerTitle}
              />
            ) : (
              <div className={s.label} onClick={handleDblClickHeaderTab}>
                {headerTitle}
              </div>
            )}
            <Upload {...props}>
              <Button icon={<UploadOutlined />}>Загрузить XML файл</Button>
            </Upload>
          </div>
          <Divider style={{ margin: 0 }} />
        </div>
        <div className={s.tabContent} id={'CDARedactor_tabContent'}>
          <XMLViewer collapsible={true} xml={code} />
        </div>
      </Spin>
    </div>
  );
};

export const CDARedactor = () => {
  const dispatch = useAppDispatch();
  // const [activeKey, setActiveKey] = useState('1');
  const newTabIndex = useRef(0);
  const { cdaRedactorTabs, activeXmlTab } = useAppSelector((state) => state.tablesSlice);

  const onChange = (newActiveKey: string) => {
    dispatch(setActiveXmlTab(newActiveKey));
  };

  const add = () => {
    const newActiveKey = `${newTabIndex.current++}-${newTabIndex.current++}`;
    const newPanes = [...cdaRedactorTabs];
    newPanes.push({
      label: 'Новая вкладка',
      fileName: '',
      code: '',
      key: newActiveKey,
      closable: true,
    });
    dispatch(setCdaRedactorTabs(newPanes));
    dispatch(setActiveXmlTab(newActiveKey));
  };

  const remove = (targetKey: TargetKey) => {
    let newActiveKey = activeXmlTab;
    let lastIndex = -1;
    cdaRedactorTabs.forEach((item, i) => {
      if (item.key === targetKey) {
        lastIndex = i - 1;
      }
    });
    const newPanes = cdaRedactorTabs.filter((item) => item.key !== targetKey);
    if (newPanes.length && newActiveKey === targetKey) {
      if (lastIndex >= 0) {
        newActiveKey = newPanes[lastIndex].key;
      } else {
        newActiveKey = newPanes[0].key;
      }
    }
    dispatch(setCdaRedactorTabs(newPanes));
    dispatch(setActiveXmlTab(newActiveKey));
    // setActiveKey(newActiveKey);
  };

  const onEdit = (
    targetKey: React.MouseEvent | React.KeyboardEvent | string,
    action: 'add' | 'remove',
  ) => {
    if (action === 'add') {
      add();
    } else {
      remove(targetKey);
    }
  };

  const handleCloseTab = () => {
    if (cdaRedactorTabs?.length <= 1) {
      setTimeout(() => {
        const newActiveKey = `${newTabIndex.current++}-${newTabIndex.current++}`;
        const newPanes = [
          {
            label: 'Новая вкладка',
            fileName: '',
            code: '',
            key: newActiveKey,
            closable: true,
          },
        ];
        dispatch(setCdaRedactorTabs(newPanes));
        dispatch(setActiveXmlTab(newActiveKey));
      }, 100);
    }
  };

  const resCdaRedactor = cdaRedactorTabs.map((tab) => {
    return {
      label: tab.label,
      children: (
        <RenderTabCDARedactor id={tab.key} code={tab.code} initialHeaderTitle={tab.label} />
      ),
      key: tab.key,
      closable: tab.closable,
      closeIcon: (
        <div onClick={handleCloseTab}>
          <CloseOutlined />
        </div>
      ),
    };
  });

  return (
    <div className={s.CDARedactor} id={'CDARedactor'}>
      <Tabs
        type='editable-card'
        tabBarStyle={{ margin: 0 }}
        onChange={onChange}
        activeKey={activeXmlTab}
        onAuxClick={(e) => console.log(e)}
        destroyInactiveTabPane={true}
        onEdit={onEdit}
        items={resCdaRedactor}
      />
    </div>
  );
};
